/*
This software is Copyright ©️ 2020 The University of Southern California. All Rights Reserved. 
Permission to use, copy, modify, and distribute this software and its documentation for educational, research and non-profit purposes, without fee, and without a written agreement is hereby granted, provided that the above copyright notice and subject to the full license file found in the root of this software deliverable. Permission to make commercial use of this software may be obtained by contacting:  USC Stevens Center for Innovation University of Southern California 1150 S. Olive Street, Suite 2300, Los Angeles, CA 90115, USA Email: accounting@stevens.usc.edu

The full terms of this copyright and license should always be found in the root directory of this software deliverable as "license.txt" and if these terms are not found with this software, please contact the USC Stevens Center for the full license.
*/

import YAML from "yaml";
import * as actions from ".";
import { useAppDispatch, useAppSelector } from "../../hooks";
import {
  LoadStatus,
  Lesson,
  LessonType,
  Resource,
  MultipleChoiceLesson,
  Edits,
} from "../../../types";
import { readYamlFile } from "../../../utils";

export interface UseWithLessons {
  lessons: Record<string, Lesson>;
  lessonEdits: Record<string, Edits<Lesson>>;
  lessonLoadStatus: LoadStatus;
  loadAllLessons: () => Promise<Lesson[]>;
  loadLessonFromYaml: (data: File | string) => Promise<Lesson>;
  createLesson: (newData: Partial<Lesson>) => Promise<Lesson>;
  deleteLesson: (lesson: Lesson) => Promise<void>;
  updateLesson: (newData: Partial<Lesson>) => Promise<Lesson>;
  revertLesson: (id: string) => Promise<Lesson>;
  isLessonLaunchable: (l: Lesson) => boolean;
  newResource: (t: LessonType) => Resource;
}

export default function useWithLessons(): UseWithLessons {
  const {
    lessons: allLessons,
    lessonEdits,
    lessonLoadStatus,
  } = useAppSelector((state) => state.lessons);
  const dispatch = useAppDispatch();

  async function loadAllLessons(): Promise<Lesson[]> {
    if (lessonLoadStatus === LoadStatus.IN_PROGRESS) return [];
    return await dispatch(actions.loadLessons()).unwrap();
  }

  async function loadLessonFromYaml(file: File | string): Promise<Lesson> {
    let data;
    if (typeof file === "string") data = YAML.parse(file);
    else data = await readYamlFile(file);
    const id = typeof data.id === "string" ? data.id : "";
    const name = typeof data.name === "string" ? data.name : "";
    const desc = typeof data.desc === "string" ? data.desc : "";
    const org = typeof data.org === "string" ? data.org : "";
    const hidden = typeof data.hidden === "boolean" ? data.hidden : false;
    const deleted = typeof data.deleted === "boolean" ? data.deleted : false;
    const downloadable =
      typeof data.downloadable === "boolean" ? data.downloadable : false;
    const randomizeResources =
      typeof data.randomizeResources === "number" ? data.randomizeResources : 0;
    const remediationScore =
      typeof data.remediationScore === "number" ? data.remediationScore : 0;
    let type = LessonType.WEB;
    if (Object.values(LessonType).includes(data.type)) type = data.type;
    else throw new Error("invalid lesson type");
    const remedialLessons = [];
    if (Array.isArray(data.remedialLessons)) {
      for (const l of data.remedialLessons) {
        if (typeof l === "string" && allLessons[l]) {
          remedialLessons.push(l);
        }
      }
    }
    const traitRequirements = [];
    if (Array.isArray(data.traitRequirements)) {
      for (const l of data.traitRequirements) {
        if (typeof l === "object") {
          traitRequirements.push(l);
        }
      }
    }
    const remedialRequirements = [];
    if (Array.isArray(data.remedialRequirements)) {
      for (const l of data.remedialRequirements) {
        if (typeof l === "object") {
          remedialRequirements.push(l);
        }
      }
    }
    const resources = [];
    if (Array.isArray(data.resources)) {
      for (const l of data.resources) {
        if (typeof l === "object") {
          resources.push(l);
        }
      }
    }
    return {
      id,
      name,
      desc,
      hidden,
      deleted,
      org,
      type,
      downloadable,
      resources,
      randomizeResources,
      remediationScore,
      remedialLessons,
      remedialRequirements,
      traitRequirements,
      learnerTypes: data.learnerTypes || [],
    };
  }

  async function createLesson(data: Partial<Lesson>): Promise<Lesson> {
    if (!data.name) throw new Error("name required");
    if (!data.desc) throw new Error("desc required");
    if (!data.type) throw new Error("type required");
    return await dispatch(
      actions.addOrUpdateLesson({
        id: data.id || "",
        name: data.name || "",
        desc: data.desc || "",
        hidden: Boolean(data.hidden),
        deleted: Boolean(data.deleted),
        downloadable: Boolean(data.downloadable),
        type: data.type || LessonType.WEB,
        resources: data.resources || [newResource(data.type || LessonType.WEB)],
        randomizeResources: data.randomizeResources || 0,
        remediationScore: data.remediationScore || 0,
        remedialLessons: data.remedialLessons || [],
        remedialRequirements: data.remedialRequirements || [],
        traitRequirements: data.traitRequirements || [],
      })
    ).unwrap();
  }

  async function deleteLesson(lesson: Lesson): Promise<void> {
    await dispatch(actions.deleteLesson(lesson.id));
  }

  async function updateLesson(data: Partial<Lesson>): Promise<Lesson> {
    return await dispatch(actions.addOrUpdateLesson(data)).unwrap();
  }

  async function revertLesson(id: string): Promise<Lesson> {
    return await dispatch(actions.revertLesson(id)).unwrap();
  }

  function isLessonLaunchable(lesson: Lesson): boolean {
    if (
      !lesson ||
      !lesson.id ||
      !lesson.name ||
      !lesson.type ||
      !lesson.resources ||
      lesson.resources.length === 0
    ) {
      return false;
    }
    const type = lesson.type;
    const resources = lesson.resources;
    if (type === LessonType.WEB || type === LessonType.WEB_ACTIVE) {
      return (
        resources.length === 1 &&
        Boolean(
          resources[0].uri ||
            resources[0].assets?.some(
              (a) =>
                a.uri &&
                (a.name === "online" ||
                  a.name === "web" ||
                  a.name === "mobile" ||
                  a.name === "desktop")
            )
        )
      );
    } else if (type === LessonType.VIDEO) {
      return (
        resources.length === 1 &&
        Boolean(
          resources[0].uri ||
            resources[0].assets?.some((a) => a.type === "video" && a.uri)
        )
      );
    } else if (type === LessonType.OPENTUTOR) {
      return (
        resources.length === 1 &&
        Boolean(
          resources[0].uri ||
            resources[0].assets?.some((a) => a.name === "online" && a.uri)
        )
      );
    } else if (type === LessonType.MULTIPLE_CHOICE) {
      for (const r of resources) {
        if (!r.inlineMcq) return false;
      }
      return true;
    } else {
      return resources.every((r) => r.uri || r.assets?.some((a) => a.uri));
    }
  }

  function newResource(t: LessonType): Resource {
    const resource: Resource = {
      id: "",
      uri: "",
      duration: 0,
      explorationLevel: 0,
      assets: [],
      knowledgeComponents: [],
      firstTimeCategory: "",
      firstTimeParams: [],
      license: "",
    };
    if (t === LessonType.MULTIPLE_CHOICE) {
      const mcq: MultipleChoiceLesson = {
        scoreMode: "",
        question: {
          type: "question",
          question: "",
          imagePath: "",
          imageHeader: "",
          startTime: 0,
          endTime: 0,
        },
        answers: [],
        hints: [],
        relatedLessons: [],
        isRandomized: false,
      };
      resource.inlineMcq = mcq;
    }
    return resource;
  }

  return {
    lessons: allLessons,
    lessonEdits,
    lessonLoadStatus,
    loadAllLessons,
    loadLessonFromYaml,
    createLesson,
    deleteLesson,
    updateLesson,
    revertLesson,
    isLessonLaunchable,
    newResource,
  };
}
